import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { companyInputSchema, type CompanyDto, type CompanyInput } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { EmptyState, ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof companyInputSchema>

const EMPTY: FormValues = { name: '', hebrewName: '', companyNumber: '', entityNumber: '' }

type Company = CompanyDto & { displayName: string; caseCount: number }

function CompanyDialog({
  open,
  clientId,
  company,
  onClose,
}: {
  open: boolean
  clientId: string
  company: Company | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const form = useForm<FormValues, unknown, CompanyInput>({
    resolver: formResolver(companyInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    form.reset(
      company
        ? {
            name: company.name ?? '',
            hebrewName: company.hebrewName ?? '',
            companyNumber: company.companyNumber ?? '',
            entityNumber: company.entityNumber ?? '',
          }
        : EMPTY
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id])

  const save = useMutation({
    mutationFn: (values: CompanyInput) =>
      company
        ? api.patch(`/clients/${clientId}/companies/${company.id}`, values)
        : api.post(`/clients/${clientId}/companies`, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.client(clientId) })
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{company ? 'עריכת חברה' : 'חברה חדשה'}</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="שם בעברית"
                autoFocus
                {...form.register('hebrewName')}
                error={Boolean(errors.hebrewName)}
                helperText={errors.hebrewName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="שם באנגלית" {...form.register('name')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="מספר חברה (ח״פ)"
                {...form.register('companyNumber')}
                error={Boolean(errors.companyNumber)}
                helperText={errors.companyNumber?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="מספר ישות" {...form.register('entityNumber')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            שמירה
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** Companies belong to a client and are managed only from the client card (spec §3.2). */
export function CompaniesPanel({ clientId, companies }: { clientId: string; companies: Company[] }) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Company | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Company | null>(null)

  const remove = useMutation({
    mutationFn: (companyId: string) => api.del(`/clients/${clientId}/companies/${companyId}`),
    onSuccess: () => {
      setPendingDelete(null)
      void queryClient.invalidateQueries({ queryKey: qk.client(clientId) })
    },
  })

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          הוספת חברה
        </Button>
      </Stack>

      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      {companies.length === 0 ? (
        <EmptyState title="אין חברות ללקוח זה" hint="חברה מאפשרת ללקוח לפעול בתיק דרך החברה" />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>שם</TableCell>
              <TableCell>שם באנגלית</TableCell>
              <TableCell>ח״פ</TableCell>
              <TableCell>מספר ישות</TableCell>
              <TableCell>תיקים</TableCell>
              <TableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.id} hover>
                <TableCell>{company.displayName}</TableCell>
                <TableCell>{company.name || '—'}</TableCell>
                <TableCell>{company.companyNumber || '—'}</TableCell>
                <TableCell>{company.entityNumber || '—'}</TableCell>
                <TableCell>{company.caseCount}</TableCell>
                <TableCell align="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditing(company)
                      setDialogOpen(true)
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setPendingDelete(company)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CompanyDialog
        open={dialogOpen}
        clientId={clientId}
        company={editing}
        onClose={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="מחיקת חברה"
        message={`החברה "${pendingDelete?.displayName ?? ''}" תימחק. שיוכים של החברה בתיקים יתרוקנו.`}
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />
    </>
  )
}
